import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, ExternalLink, ImageOff, MoreHorizontal, RefreshCw, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { ConfirmActionDialog, DESTRUCTIVE_OUTLINE, RelativeTime, StatusBadge, formatDate, formatDateTime, formatNumber } from '../../../components/design-system/admin';
import { useAdminSignupsApi } from '../../../common/hooks/use-admin-signups-api.hook';
import { EVIDENCE_SIGNALS, SignupDetail, SignupRow, demoId } from '../../../common/models/admin-signups.model';
import { BlockSignupDialog } from './BlockSignupDialog';
import { ApproveSignupDialog } from '../pricing/ApproveSignupDialog';
import { SdkPresenceSummary, SignupProgress, SignupStatusBadge, VERDICT_TONE, VerdictChip, enumKey, useSdkCheck } from './signup-parts';

const SECTION = 'text-xs font-medium text-muted-foreground';
/** Re-check runs in the background (< 20 s): the sheet looks again after this. */
const RECHECK_REFRESH_MS = 20_000;

const Fact = ({ label, children }: { label: ReactNode; children: ReactNode }) => (
  <div className="flex items-start justify-between gap-4 py-1">
    <dt className="shrink-0 text-muted-foreground">{label}</dt>
    <dd className="min-w-0 text-end break-words">{children}</dd>
  </div>
);

const Section = ({ title, children, testId }: { title: ReactNode; children: ReactNode; testId?: string }) => (
  <section className="grid gap-2" data-testid={testId}>
    <h3 className={SECTION}>{title}</h3>
    {children}
  </section>
);

const siteUrl = (domain?: string, url?: string) => url || (domain ? `https://${domain}` : undefined);

/**
 * One signup, everything a reviewer needs to decide in one look: the
 * homepage, the verdict and why, the account, what happened when — and the
 * decision itself in the footer (docs/plg-review.md → Super-admin).
 */
export function SignupDetailSheet({
  vendorId,
  row,
  onClose,
  onChanged,
}: {
  /** The account to show; null closes the sheet (unless a visitor `row` is shown). */
  vendorId: string | null;
  /** What the list already knows: painted at once, and all there is for a visitor. */
  row?: SignupRow | null;
  onClose: () => void;
  /** After an action: the fresh detail, for the list and the counts. */
  onChanged?: (detail: SignupDetail | null) => void;
}) {
  const { t } = useTranslation();
  const api = useAdminSignupsApi();
  const apiRef = useRef(api);
  apiRef.current = api;

  const [detail, setDetail] = useState<SignupDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [blockOpen, setBlockOpen] = useState(false);
  // Approving asks for the trial length and this vendor's prices (docs/pricing-v2.md).
  const [approveOpen, setApproveOpen] = useState(false);
  // Approving an account whose snippet is not on its site right now asks first.
  const [sdkWarnOpen, setSdkWarnOpen] = useState(false);
  const runSdkCheck = useSdkCheck();
  const recheckTimer = useRef<number | undefined>(undefined);

  const open = Boolean(vendorId || row);

  const load = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const d = await apiRef.current.detail(id);
      setDetail(d || null);
      return d || null;
    } catch {
      toast.error(t('ADMIN_SIGNUPS.TOAST.DETAIL_FAILED'));
      return null;
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDetail(null);
    window.clearTimeout(recheckTimer.current);
    if (vendorId) load(vendorId);
  }, [vendorId, load]);

  useEffect(() => () => window.clearTimeout(recheckTimer.current), []);

  /** Run an action; the answer is the new detail, or we read it again. */
  const act = async (name: string, run: () => Promise<any>, success: string) => {
    if (!vendorId) return;
    setBusy(name);
    try {
      const res = await run();
      const next = res && typeof res === 'object' && 'id' in res ? (res as SignupDetail) : await load(vendorId);
      if (next) setDetail(next);
      toast.success(success);
      onChanged?.(next);
      return true;
    } catch (e: any) {
      const body = await Promise.resolve(e?.json?.()).catch(() => null);
      toast.error(body?.message || t('ADMIN_SIGNUPS.TOAST.ACTION_FAILED'));
      return false;
    } finally {
      setBusy(null);
    }
  };

  const recheck = async () => {
    if (!vendorId) return;
    setBusy('recheck');
    try {
      await api.recheck(vendorId);
      toast.success(t('ADMIN_SIGNUPS.TOAST.RECHECK_STARTED'));
      window.clearTimeout(recheckTimer.current);
      recheckTimer.current = window.setTimeout(() => load(vendorId), RECHECK_REFRESH_MS);
    } catch {
      toast.error(t('ADMIN_SIGNUPS.TOAST.ACTION_FAILED'));
    } finally {
      setBusy(null);
    }
  };

  const checkSdk = async () => {
    if (!vendorId) return;
    setBusy('sdk-check');
    try {
      const next = await runSdkCheck(vendorId);
      if (next) {
        setDetail(next);
        onChanged?.(next);
      }
    } finally {
      setBusy(null);
    }
  };

  const shown: SignupRow | null = detail ?? row ?? null;
  const evidence = detail?.selfServe?.evidence;
  const review = detail?.selfServe?.review ?? null;
  const status = shown?.status;
  const isAccount = Boolean(vendorId) && shown?.kind !== 'visitor';
  const href = siteUrl(shown?.domain, evidence?.url);
  const plan = detail?.plan ?? detail?.planSlug ?? null;
  const presence = detail?.sdkPresence ?? shown?.sdkPresence ?? null;
  const presenceDomain = presence?.domain || shown?.domain || t('ADMIN_SIGNUPS.TABLE.NO_DOMAIN');
  // An older API sends no presence: nothing to warn about then.
  const sdkNotLive = Boolean(presence && presence.status !== 'live');
  const startApprove = () => (sdkNotLive ? setSdkWarnOpen(true) : setApproveOpen(true));

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="gap-0 p-0 text-sm data-[side=right]:w-full data-[side=right]:sm:max-w-[560px]" data-testid="signup-sheet">
        <SheetHeader className="gap-1 border-b p-6 pe-12">
          <div className="flex items-start justify-between gap-3">
            <SheetTitle className="min-w-0 truncate font-heading text-xl font-medium tracking-tight" dir="ltr">
              {shown?.domain || t('ADMIN_SIGNUPS.TABLE.NO_DOMAIN')}
            </SheetTitle>
            {status && <SignupStatusBadge status={status} className="mt-1.5" />}
          </div>
          <SheetDescription className="truncate text-sm">{evidence?.title || shown?.title || t('ADMIN_SIGNUPS.DETAIL.NO_TITLE')}</SheetDescription>
          {shown && (
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <SignupProgress row={shown} />
              <VerdictChip verdict={shown.verdict} />
            </div>
          )}
        </SheetHeader>

        {/* Children keep their size: a scrolling flex column would squash the screenshot box flat. */}
        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-6 [&>*]:shrink-0">
          {loading && !detail ? (
            <div className="grid gap-3" data-testid="signup-sheet-loading">
              <Skeleton className="aspect-[16/10] w-full rounded-lg" />
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          ) : null}

          {/* The homepage as the reviewer would see it. */}
          {isAccount && detail && (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative block overflow-hidden rounded-lg border bg-muted"
              aria-label={t('ADMIN_SIGNUPS.DETAIL.OPEN_SITE')}
              data-testid="signup-screenshot"
            >
              {evidence?.screenshotUrl ? (
                <img src={evidence.screenshotUrl} alt="" className="aspect-[16/10] w-full object-cover object-top" loading="lazy" />
              ) : (
                <span className="flex aspect-[16/10] w-full flex-col items-center justify-center gap-2 text-muted-foreground">
                  <ImageOff className="size-5" />
                  <span className="text-xs">{t('ADMIN_SIGNUPS.DETAIL.NO_SCREENSHOT')}</span>
                </span>
              )}
              <span className="absolute end-2 bottom-2 inline-flex items-center gap-1 rounded-md border bg-background px-2 py-1 text-xs opacity-90 group-hover:opacity-100">
                <ExternalLink className="size-3" />
                {t('ADMIN_SIGNUPS.DETAIL.OPEN_SITE')}
              </span>
            </a>
          )}

          {isAccount && detail && (
            <Section title={t('ADMIN_SIGNUPS.SDK.TITLE')} testId="signup-sdk">
              <SdkPresenceSummary presence={presence} checking={busy === 'sdk-check'} onCheck={busy ? undefined : checkSdk} testId="signup-sdk-check" />
              <dl className="grid">
                <Fact label={t('ADMIN_SIGNUPS.SDK.FIRST_SEEN')}>{presence?.firstSeenAt ? formatDateTime(presence.firstSeenAt) : t('ADMIN_SIGNUPS.SDK.NEVER_SEEN')}</Fact>
                <Fact label={t('ADMIN_SIGNUPS.SDK.LAST_SEEN')}>
                  {presence?.lastSeenAt ? (
                    <span className="flex flex-col items-end">
                      <RelativeTime value={presence.lastSeenAt} />
                      {presence.domain && (
                        <span dir="ltr" className="text-xs text-muted-foreground">
                          {presence.domain}
                        </span>
                      )}
                    </span>
                  ) : (
                    t('ADMIN_SIGNUPS.SDK.NEVER_SEEN')
                  )}
                </Fact>
                <Fact label={t('ADMIN_SIGNUPS.SDK.LAST_CHECK')}>
                  {presence?.lastCheck ? (
                    <span className="flex flex-col items-end" data-testid="signup-sdk-last-check">
                      <span>
                        {presence.lastCheck.found
                          ? t('ADMIN_SIGNUPS.SDK.CHECK_FOUND')
                          : presence.lastCheck.error
                            ? t(`ADMIN_SIGNUPS.SDK.ERROR.${enumKey(presence.lastCheck.error)}`, { defaultValue: presence.lastCheck.error })
                            : t('ADMIN_SIGNUPS.SDK.CHECK_NOT_FOUND')}
                        {' · '}
                        <RelativeTime value={presence.lastCheck.at} />
                      </span>
                      {presence.lastCheck.url && (
                        <a href={presence.lastCheck.url} target="_blank" rel="noopener noreferrer" dir="ltr" className="text-xs break-all text-muted-foreground underline-offset-2 hover:underline">
                          {presence.lastCheck.url}
                        </a>
                      )}
                    </span>
                  ) : (
                    t('ADMIN_SIGNUPS.SDK.NOT_CHECKED')
                  )}
                </Fact>
              </dl>
            </Section>
          )}

          {isAccount && detail && (
            <Section title={t('ADMIN_SIGNUPS.DETAIL.EVIDENCE')} testId="signup-evidence">
              {evidence ? (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge tone={VERDICT_TONE[evidence.verdict] ?? 'neutral'}>{t(`ADMIN_SIGNUPS.VERDICT.${enumKey(evidence.verdict)}`)}</StatusBadge>
                    <span className="text-xs text-muted-foreground tabular-nums">{t('ADMIN_SIGNUPS.DETAIL.SCORE', { score: evidence.score })}</span>
                    {evidence.ai && (
                      <span className="text-xs text-muted-foreground">
                        · {t('ADMIN_SIGNUPS.DETAIL.AI_VERDICT', { verdict: t(`ADMIN_SIGNUPS.VERDICT.${enumKey(evidence.ai.verdict)}`) })}
                      </span>
                    )}
                    <span className="ms-auto text-xs text-muted-foreground">{t('ADMIN_SIGNUPS.DETAIL.CHECKED_AT', { date: formatDateTime(evidence.checkedAt) })}</span>
                  </div>
                  {evidence.ai && (
                    <div className="grid gap-1 rounded-lg bg-muted p-3" data-testid="signup-ai-summary">
                      <p className="leading-relaxed">{evidence.ai.summary}</p>
                      <p className="text-xs text-muted-foreground">
                        {[
                          evidence.ai.sells ? t('ADMIN_SIGNUPS.DETAIL.AI_SELLS', { sells: evidence.ai.sells }) : null,
                          t('ADMIN_SIGNUPS.DETAIL.AI_FIT', { fit: t(`ADMIN_SIGNUPS.FIT.${enumKey(evidence.ai.fit)}`) }),
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    </div>
                  )}
                  {evidence.unreadable && (
                    <p className="flex items-start gap-2 rounded-lg border p-3 leading-relaxed" data-testid="signup-unreadable">
                      <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-warning" aria-hidden />
                      {t('ADMIN_SIGNUPS.DETAIL.UNREADABLE')}
                    </p>
                  )}
                  {evidence.error && <p className="text-xs text-muted-foreground">{t('ADMIN_SIGNUPS.DETAIL.EVIDENCE_ERROR', { error: evidence.error })}</p>}

                  <ul className="grid grid-cols-1 gap-x-4 gap-y-1.5 sm:grid-cols-2" data-testid="signup-signals">
                    {EVIDENCE_SIGNALS.map((s) => {
                      const on = Boolean(evidence.signals?.[s]);
                      return (
                        <li key={s} className="flex items-center gap-2" data-signal={s} data-on={on ? 'true' : 'false'}>
                          {on ? <Check className="size-3.5 shrink-0 text-success" aria-hidden /> : <X className="size-3.5 shrink-0 text-muted-foreground/60" aria-hidden />}
                          <span className={on ? '' : 'text-muted-foreground'}>{t(`ADMIN_SIGNUPS.SIGNAL.${enumKey(s)}`)}</span>
                          <span className="sr-only">{on ? t('ADMIN_SIGNUPS.DETAIL.YES') : t('ADMIN_SIGNUPS.DETAIL.NO')}</span>
                        </li>
                      );
                    })}
                  </ul>

                  <dl className="mt-1 grid border-t pt-2">
                    <Fact label={t('ADMIN_SIGNUPS.DETAIL.DOMAIN_AGE')}>
                      {evidence.domain?.ageDays != null
                        ? `${t('ADMIN_SIGNUPS.DETAIL.DAYS', { count: evidence.domain.ageDays })}${evidence.domain.registeredAt ? ` · ${t('ADMIN_SIGNUPS.DETAIL.REGISTERED', { date: formatDate(evidence.domain.registeredAt) })}` : ''}`
                        : t('ADMIN_SIGNUPS.DETAIL.UNKNOWN')}
                    </Fact>
                    {evidence.domain?.registrar && <Fact label={t('ADMIN_SIGNUPS.DETAIL.REGISTRAR')}>{evidence.domain.registrar}</Fact>}
                    <Fact label={t('ADMIN_SIGNUPS.DETAIL.SITEMAP_URLS')}>{formatNumber(evidence.pages?.sitemapUrls)}</Fact>
                    <Fact label={t('ADMIN_SIGNUPS.DETAIL.PRODUCT_URLS')}>{formatNumber(evidence.pages?.productUrls)}</Fact>
                    <Fact label={t('ADMIN_SIGNUPS.DETAIL.PAGE_CONTENT')}>
                      {t('ADMIN_SIGNUPS.DETAIL.PAGE_CONTENT_VALUE', {
                        words: formatNumber(evidence.pages?.words),
                        images: formatNumber(evidence.pages?.images),
                        links: formatNumber(evidence.pages?.internalLinks),
                      })}
                    </Fact>
                    <Fact label={t('ADMIN_SIGNUPS.DETAIL.PLATFORM')}>{evidence.platform || shown?.platform || t('ADMIN_SIGNUPS.DETAIL.UNKNOWN')}</Fact>
                    <Fact label={t('ADMIN_SIGNUPS.DETAIL.SDK_SEEN')}>
                      {evidence.sdkSeenFrom?.origin || evidence.sdkSeenFrom?.page ? (
                        <span className="flex flex-col items-end">
                          <span dir="ltr" className="break-all">{evidence.sdkSeenFrom.page || evidence.sdkSeenFrom.origin}</span>
                          {evidence.sdkSeenFrom.at && <span className="text-xs text-muted-foreground">{formatDateTime(evidence.sdkSeenFrom.at)}</span>}
                        </span>
                      ) : (
                        t('ADMIN_SIGNUPS.DETAIL.SDK_NOT_SEEN')
                      )}
                    </Fact>
                  </dl>
                </>
              ) : (
                <p className="text-muted-foreground" data-testid="signup-no-evidence">
                  {t('ADMIN_SIGNUPS.DETAIL.NO_EVIDENCE')}
                </p>
              )}
            </Section>
          )}

          {isAccount && review && review.status !== 'pending' && (
            <Section title={t('ADMIN_SIGNUPS.DETAIL.REVIEW')} testId="signup-review">
              <dl className="grid">
                <Fact label={t('ADMIN_SIGNUPS.DETAIL.DECISION')}>
                  <StatusBadge tone={review.status === 'approved' ? 'success' : 'danger'}>{t(`ADMIN_SIGNUPS.DECISION.${enumKey(review.status)}`)}</StatusBadge>
                </Fact>
                <Fact label={t('ADMIN_SIGNUPS.DETAIL.DECIDED_BY')}>
                  {review.decidedBy === 'auto' ? t('ADMIN_SIGNUPS.DETAIL.AUTO') : review.decidedByName || '—'}
                  {review.decidedAt ? ` · ${formatDateTime(review.decidedAt)}` : ''}
                </Fact>
                {review.reason && <Fact label={t('ADMIN_SIGNUPS.DETAIL.REASON')}>{t(`ADMIN_SIGNUPS.REASON.${enumKey(review.reason)}`)}</Fact>}
              </dl>
              {review.message && (
                <div className="grid gap-1">
                  <span className={SECTION}>{t('ADMIN_SIGNUPS.DETAIL.MESSAGE_TO_VENDOR')}</span>
                  <p className="rounded-lg bg-muted p-3 whitespace-pre-wrap">{review.message}</p>
                </div>
              )}
              {review.note && (
                <div className="grid gap-1">
                  <span className={SECTION}>{t('ADMIN_SIGNUPS.DETAIL.INTERNAL_NOTE')}</span>
                  <p className="rounded-lg border border-dashed p-3 whitespace-pre-wrap">{review.note}</p>
                </div>
              )}
            </Section>
          )}

          {isAccount && detail && (
            <Section title={t('ADMIN_SIGNUPS.DETAIL.ACCOUNT')} testId="signup-account">
              <dl className="grid">
                {(detail.users?.length ? detail.users : [{ id: 'row', email: detail.email || '', name: detail.name, isVerified: Boolean(detail.steps?.emailVerified) }]).map((u) => (
                  <Fact key={u.id} label={t('ADMIN_SIGNUPS.DETAIL.EMAIL')}>
                    <span dir="ltr" className="select-all">
                      {u.email || '—'}
                    </span>{' '}
                    <StatusBadge tone={u.isVerified ? 'success' : 'warning'} className="ms-1 align-middle">
                      {u.isVerified ? t('ADMIN_SIGNUPS.DETAIL.VERIFIED') : t('ADMIN_SIGNUPS.DETAIL.NOT_VERIFIED')}
                    </StatusBadge>
                  </Fact>
                ))}
                <Fact label={t('ADMIN_SIGNUPS.DETAIL.NAME')}>{detail.name || detail.users?.[0]?.name || '—'}</Fact>
                <Fact label={t('ADMIN_SIGNUPS.DETAIL.LANGUAGE')}>{detail.locale ? detail.locale.toUpperCase() : '—'}</Fact>
                <Fact label={t('ADMIN_SIGNUPS.DETAIL.IP')}>
                  <span dir="ltr">{detail.selfServe?.signupIp || '—'}</span>
                </Fact>
                <Fact label={t('ADMIN_SIGNUPS.DETAIL.RISK_FLAGS')}>
                  {(detail.selfServe?.riskFlags ?? detail.riskFlags ?? []).length ? (
                    <span className="inline-flex flex-wrap justify-end gap-1">
                      {(detail.selfServe?.riskFlags ?? detail.riskFlags ?? []).map((f) => (
                        <span key={f} className="rounded-md border px-1.5 py-0.5 text-xs">
                          {f}
                        </span>
                      ))}
                    </span>
                  ) : (
                    t('ADMIN_SIGNUPS.DETAIL.NONE')
                  )}
                </Fact>
                <Fact label={t('ADMIN_SIGNUPS.DETAIL.PLAN')}>{plan || '—'}</Fact>
                <Fact label={t('ADMIN_SIGNUPS.DETAIL.SIGNED_UP')}>
                  <RelativeTime value={detail.createdAt} />
                </Fact>
                {detail.onboardedAt && <Fact label={t('ADMIN_SIGNUPS.STEP.ONBOARDED')}>{formatDateTime(detail.onboardedAt)}</Fact>}
              </dl>
            </Section>
          )}

          {!isAccount && shown && (
            <Section title={t('ADMIN_SIGNUPS.DETAIL.VISITOR')} testId="signup-visitor">
              <p className="text-muted-foreground">{t('ADMIN_SIGNUPS.DETAIL.VISITOR_NOTE')}</p>
              <dl className="grid">
                <Fact label={t('ADMIN_SIGNUPS.DETAIL.WEBSITE_CHECKS')}>{formatNumber(shown.websiteChecks ?? 0)}</Fact>
                <Fact label={t('ADMIN_SIGNUPS.DETAIL.PLATFORM')}>{shown.platform || t('ADMIN_SIGNUPS.DETAIL.UNKNOWN')}</Fact>
                <Fact label={t('ADMIN_SIGNUPS.DETAIL.FIRST_SEEN')}>
                  <RelativeTime value={shown.createdAt} />
                </Fact>
                <Fact label={t('ADMIN_SIGNUPS.DETAIL.LAST_SEEN')}>
                  <RelativeTime value={shown.lastActivityAt} />
                </Fact>
              </dl>
            </Section>
          )}

          {isAccount && detail && (
            <Section title={t('ADMIN_SIGNUPS.DETAIL.TIMELINE')} testId="signup-timeline">
              {detail.events?.length ? (
                <ol className="grid gap-2 border-s ps-4">
                  {detail.events.map((e, i) => (
                    <li key={`${e.type}-${e.at}-${i}`} className="relative">
                      <span className="absolute -start-[21px] top-1.5 size-2 rounded-full border bg-background" aria-hidden />
                      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                        <span>{t(`ADMIN_SIGNUPS.EVENT.${enumKey(e.type)}`, { defaultValue: e.type, ...eventVars(e, t) })}</span>
                        <span className="text-xs text-muted-foreground tabular-nums">{formatDateTime(e.at)}</span>
                      </div>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="text-muted-foreground">{t('ADMIN_SIGNUPS.DETAIL.NO_EVENTS')}</p>
              )}
            </Section>
          )}

          {isAccount && detail && (
            <Section title={t('ADMIN_SIGNUPS.DETAIL.DEMO_REQUESTS')} testId="signup-demos">
              {detail.demoRequests?.length ? (
                <ul className="grid gap-2">
                  {detail.demoRequests.map((d) => (
                    <li key={demoId(d) || d.createdAt} className="rounded-lg border p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-medium">{d.name}</span>
                        <StatusBadge tone={d.status === 'new' ? 'warning' : d.status === 'lost' ? 'neutral' : 'success'}>{t(`ADMIN_SIGNUPS.DEMO.STATUS.${enumKey(d.status)}`)}</StatusBadge>
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {t(`ADMIN_SIGNUPS.DEMO.REASONS.${enumKey(d.reason)}`)} · {t(`ADMIN_SIGNUPS.DEMO.SELLS.${enumKey(d.sells)}`)} · {formatDateTime(d.createdAt)}
                      </p>
                      {d.message && <p className="mt-2 whitespace-pre-wrap">{d.message}</p>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground">{t('ADMIN_SIGNUPS.DETAIL.NO_DEMOS')}</p>
              )}
            </Section>
          )}
        </div>

        {isAccount && detail && (
          <SheetFooter className="mt-0 flex-row flex-wrap items-center gap-2 border-t p-4" data-testid="signup-actions">
            {status === 'pending_review' && (
              <Button
                variant="outline"
                disabled={Boolean(busy)}
                onClick={startApprove}
                data-testid="signup-approve"
              >
                {t('ADMIN_SIGNUPS.ACTION.APPROVE')}
              </Button>
            )}
            {status !== 'blocked' && (
              <Button variant="outline" className={DESTRUCTIVE_OUTLINE} disabled={Boolean(busy)} onClick={() => setBlockOpen(true)} data-testid="signup-block">
                {t('ADMIN_SIGNUPS.ACTION.BLOCK')}
              </Button>
            )}
            <Button variant="outline" disabled={Boolean(busy)} onClick={recheck} data-testid="signup-recheck">
              <RefreshCw data-icon="inline-start" className={busy === 'recheck' ? 'animate-spin' : undefined} />
              {t('ADMIN_SIGNUPS.ACTION.RECHECK')}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="ms-auto" aria-label={t('ADMIN_SIGNUPS.ACTION.MORE')} disabled={Boolean(busy)} data-testid="signup-more">
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {!detail.steps?.emailVerified && (
                  <DropdownMenuItem onSelect={() => act('verify', () => api.markEmailVerified(vendorId as string), t('ADMIN_SIGNUPS.TOAST.MARKED_VERIFIED'))}>
                    {t('ADMIN_SIGNUPS.ACTION.MARK_VERIFIED')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={() => act('resend', () => api.resendVerification(vendorId as string), t('ADMIN_SIGNUPS.TOAST.VERIFICATION_SENT'))}>
                  {t('ADMIN_SIGNUPS.ACTION.RESEND')}
                </DropdownMenuItem>
                {status === 'blocked' && (
                  <DropdownMenuItem onSelect={() => act('reopen', () => api.reopen(vendorId as string), t('ADMIN_SIGNUPS.TOAST.REOPENED'))} data-testid="signup-reopen">
                    {t('ADMIN_SIGNUPS.ACTION.REOPEN')}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </SheetFooter>
        )}

        <ConfirmActionDialog
          open={sdkWarnOpen}
          onOpenChange={setSdkWarnOpen}
          title={t('ADMIN_SIGNUPS.SDK.APPROVE_WARN.TITLE')}
          description={
            presence?.status === 'quiet'
              ? t('ADMIN_SIGNUPS.SDK.APPROVE_WARN.QUIET', { domain: presenceDomain, date: formatDate(presence.lastSeenAt) })
              : presence?.status === 'removed'
                ? t('ADMIN_SIGNUPS.SDK.APPROVE_WARN.REMOVED', { domain: presenceDomain })
                : t('ADMIN_SIGNUPS.SDK.APPROVE_WARN.NEVER', { domain: presenceDomain })
          }
          confirmLabel={t('ADMIN_SIGNUPS.SDK.APPROVE_WARN.CONFIRM')}
          onConfirm={() => {
            setSdkWarnOpen(false);
            setApproveOpen(true);
          }}
        >
          <p className="text-sm text-muted-foreground" data-testid="sdk-approve-warning">
            {t('ADMIN_SIGNUPS.SDK.APPROVE_WARN.HINT')}
          </p>
        </ConfirmActionDialog>
        <ApproveSignupDialog
          open={approveOpen}
          onOpenChange={setApproveOpen}
          domain={shown?.domain}
          busy={busy === 'approve'}
          onApprove={async (body) => {
            const ok = await act('approve', () => api.approve(vendorId as string, body), t('ADMIN_SIGNUPS.TOAST.APPROVED'));
            if (ok) setApproveOpen(false);
          }}
        />
        <BlockSignupDialog
          open={blockOpen}
          onOpenChange={setBlockOpen}
          domain={shown?.domain}
          busy={busy === 'block'}
          onConfirm={async (payload) => {
            const ok = await act('block', () => api.block(vendorId as string, payload), t('ADMIN_SIGNUPS.TOAST.BLOCKED'));
            if (ok) setBlockOpen(false);
          }}
        />
      </SheetContent>
    </Sheet>
  );
}

/** The few details an event's line names ("Blocked by Sara — no business"). */
function eventVars(e: { data?: Record<string, any> }, translate: (k: string, o?: any) => unknown): Record<string, string> {
  const t = (k: string, o?: any) => String(translate(k, o));
  const d = e.data || {};
  return {
    by: String(d.byName || d.by || ''),
    origin: String(d.origin || ''),
    verdict: d.verdict ? t(`ADMIN_SIGNUPS.VERDICT.${enumKey(d.verdict)}`) : '',
    score: d.score != null ? String(d.score) : '',
    reason: d.reason ? t(`ADMIN_SIGNUPS.REASON.${enumKey(d.reason)}`, { defaultValue: String(d.reason) }) : '',
    found: d.found ? t('ADMIN_SIGNUPS.DETAIL.YES') : t('ADMIN_SIGNUPS.DETAIL.NO'),
  };
}
